"use client";

import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** The look of the buy button it stands in for, without hover effects; it is dimmed on top. */
  className?: string;
  style?: CSSProperties;
  /** "block" fills its row like a card button; "inline" sits in a line of text. */
  display?: "block" | "inline";
};

/**
 * The buy button of a plan that isn't on sale yet (see isPlanForSale). It
 * looks disabled and says "Coming soon" on hover, keyboard focus or a tap.
 *
 * It has no purchase handler at all: deleting aria-disabled or its classes in
 * dev tools leaves a button whose only effect is this popover. The server
 * refuses the purchase too (/api/checkout and /api/billing/change-plan answer
 * 403 coming_soon). aria-disabled rather than `disabled` keeps it focusable,
 * so a keyboard can reach the popover, and lets the browser send it mouse
 * events (a `disabled` button gets none in some browsers).
 */
export function ComingSoonButton({ children, className = "", style, display = "block" }: Props) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Touch has no mouseleave: a tap anywhere else, or Escape, closes it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={`relative ${display === "block" ? "block" : "inline-block"}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-disabled="true"
        aria-describedby={tipId}
        data-coming-soon=""
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={`${className} cursor-not-allowed opacity-50`}
        style={style}
      >
        {children}
      </button>
      <span
        id={tipId}
        role="tooltip"
        className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-[#3a5a78] bg-[#1b2838] px-2.5 py-1.5 text-xs font-medium text-[#e8eef3] shadow-2xl ${
          open ? "" : "hidden"
        }`}
      >
        Coming soon
      </span>
    </span>
  );
}
